import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { toast } from "sonner";
import WishlinkLogo from "../components/WishlinkLogo";

function AuthShell({ title, subtitle, children, footer }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "var(--bg-page)" }}>
      <div className="w-full max-w-md">
        <div className="mb-8 flex flex-col items-center">
          <WishlinkLogo
            className="h-14 w-auto max-w-[240px] object-contain"
            fallbackClassName="font-display font-semibold text-5xl tracking-tight text-[var(--brand)]"
          />
          <span className="mt-1.5 text-xs font-semibold tracking-[0.24em] uppercase text-[var(--brand)]" data-testid="kiwi-wordmark">Kiwi</span>
        </div>
        <div className="wl-card p-8">
          <h1 className="font-display text-2xl font-semibold mb-6">{title}</h1>
          {children}
        </div>
        {footer && <div className="mt-6 text-sm text-[var(--text-secondary)] text-center">{footer}</div>}
      </div>
    </div>
  );
}

export function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const { login, user } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    if (user?.type === "admin") nav("/admin/campaigns", { replace: true });
    else if (user?.type === "brand") nav("/campaigns", { replace: true });
  }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const u = await login(email, password);
      toast.success("Signed in");
      nav(u.type === "admin" ? "/admin/campaigns" : "/campaigns", { replace: true });
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Invalid email or password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell
      title="Sign in"
      subtitle="Welcome to Wishlink Kiwi."
      footer={<span><Link className="text-[var(--brand)] font-medium" to="/forgot-password" data-testid="forgot-link">Forgot password?</Link></span>}
    >
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="wl-label">Email</label>
          <input className="wl-input mt-1" value={email} onChange={(e) => setEmail(e.target.value)}
            type="email" required placeholder="you@brand.com" data-testid="login-email" />
        </div>
        <div>
          <label className="wl-label">Password</label>
          <input className="wl-input mt-1" value={password} onChange={(e) => setPassword(e.target.value)}
            type="password" required placeholder="••••••••" data-testid="login-password" />
        </div>
        {err && <div className="text-sm text-[#991b1b]" data-testid="login-error">{err}</div>}
        <button type="submit" className="wl-btn-primary w-full" disabled={busy} data-testid="login-submit">
          {busy ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </AuthShell>
  );
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setSent(data.stub_token || true);
      toast.success("If that email exists, a reset link has been sent.");
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Failed to send reset");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title="Reset password" subtitle="Enter your email to receive a reset link."
      footer={<Link className="text-[var(--brand)] font-medium" to="/login">Back to sign in</Link>}>
      {sent ? (
        <div className="space-y-3" data-testid="forgot-confirm">
          <div className="text-sm">If <strong>{email}</strong> exists in our system, a reset link has been sent.</div>
          {typeof sent === "string" && (
            <div className="wl-card-solid p-3 text-xs">
              <div className="font-semibold mb-1">Prototype mode — reset link:</div>
              <Link className="text-[var(--brand)] break-all" to={`/reset-password/${sent}`} data-testid="forgot-stub-link">/reset-password/{sent}</Link>
            </div>
          )}
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="wl-label">Email</label>
            <input className="wl-input mt-1" value={email} onChange={(e) => setEmail(e.target.value)} type="email" required data-testid="forgot-email" />
          </div>
          <button type="submit" className="wl-btn-primary w-full" disabled={busy} data-testid="forgot-submit">
            {busy ? "Sending…" : "Send reset link"}
          </button>
        </form>
      )}
    </AuthShell>
  );
}

export function ResetPasswordPage() {
  const { token } = useParams();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) return setErr("Password must be at least 8 characters");
    if (pw !== pw2) return setErr("Passwords don't match");
    setBusy(true); setErr("");
    try {
      await api.post("/auth/reset-password", { token, password: pw });
      toast.success("Password reset. Please sign in.");
      nav("/login", { replace: true });
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Reset failed");
    } finally { setBusy(false); }
  };

  return (
    <AuthShell title="Set new password">
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="wl-label">New password</label>
          <input className="wl-input mt-1" type="password" minLength={8} value={pw} onChange={(e) => setPw(e.target.value)} required data-testid="reset-pw" />
        </div>
        <div>
          <label className="wl-label">Confirm password</label>
          <input className="wl-input mt-1" type="password" minLength={8} value={pw2} onChange={(e) => setPw2(e.target.value)} required data-testid="reset-pw2" />
        </div>
        {err && <div className="text-sm text-[#991b1b]" data-testid="reset-error">{err}</div>}
        <button className="wl-btn-primary w-full" disabled={busy} data-testid="reset-submit">{busy ? "Saving…" : "Save password"}</button>
      </form>
    </AuthShell>
  );
}

export function SetPasswordPage() {
  const { token } = useParams();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/auth/invite/${token}`);
        setEmail(data.email);
      } catch (ex) {
        setErr("Invalid or expired invite link.");
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) return setErr("Password must be at least 8 characters");
    if (pw !== pw2) return setErr("Passwords don't match");
    setBusy(true); setErr("");
    try {
      await api.post("/auth/set-password", { token, password: pw });
      toast.success("Account activated. Please sign in.");
      nav("/login", { replace: true });
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Set password failed");
    } finally { setBusy(false); }
  };

  return (
    <AuthShell title="Welcome to Wishlink" subtitle={email ? `Set a password for ${email}` : "Loading invite…"}>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="wl-label">New password</label>
          <input className="wl-input mt-1" type="password" minLength={8} value={pw} onChange={(e) => setPw(e.target.value)} required data-testid="set-pw" />
        </div>
        <div>
          <label className="wl-label">Confirm password</label>
          <input className="wl-input mt-1" type="password" minLength={8} value={pw2} onChange={(e) => setPw2(e.target.value)} required data-testid="set-pw2" />
        </div>
        {err && <div className="text-sm text-[#991b1b]" data-testid="set-error">{err}</div>}
        <button className="wl-btn-primary w-full" disabled={busy || !email} data-testid="set-submit">{busy ? "Saving…" : "Activate account"}</button>
      </form>
    </AuthShell>
  );
}
